import { captureError } from '@ben/api/capture-error';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { isEmpty, isNil } from 'lodash';
import useMeShared from '../use-me-shared/use-me-shared';
interface FlagRequestProperties {
  employeeId?: number;
  employerId?: number;
}

interface Toggle {
  name: string;
  enabled: boolean;
  variant: {
    name: string;
    enabled: boolean;
    feature_enabled: boolean;
    featureEnabled: boolean;
  };
  impressionData: boolean;
}

const ENABLE_ALL_LOCAL_FLAGS = '*';

export const getLocalFeatureFlagValue = (flagName: string) => {
  if (process.env.NODE_ENV !== 'development') return undefined;

  const localFeatureFlags = process.env.REACT_APP_LOCAL_FEATURE_FLAGS?.trim();
  if (!localFeatureFlags) return false;

  const enabledFlags = new Set(
    localFeatureFlags
      .split(',')
      .map((featureFlag) => featureFlag.trim())
      .filter(Boolean)
  );

  return enabledFlags.has(ENABLE_ALL_LOCAL_FLAGS) || enabledFlags.has(flagName);
};

const getFlags = async (properties: FlagRequestProperties) => {
  const environments = process.env;

  try {
    const response = await axios.post<{ toggles: Toggle[] }>(
      `${process.env.REACT_APP_UNLEASH_URL ?? process.env.REACT_APP_API_URL}unleash/api/frontend`,
      {
        context: {
          appName: environments.REACT_APP_NAME,
          properties: {
            employeeId: properties.employeeId?.toString(),
            employerId: properties.employerId?.toString(),
          },
        },
      },
      {
        headers: {
          Authorization: environments.REACT_APP_UNLEASH_CLIENT_KEY,
          'Content-Type': 'application/json',
          accept: 'application/json',
          'x-ben-application': process.env.REACT_APP_NAME,
        },
      }
    );

    return { toggles: response.data?.toggles ?? [] };
  } catch (error) {
    captureError(error);
    return { toggles: [] };
  }
};

const CACHE_KEY = 'feature-flags';

const getKey = (parameters?: Partial<FlagRequestProperties>) => {
  if (isEmpty(parameters)) return [CACHE_KEY];
  return [CACHE_KEY, parameters];
};

const useFeatureFlag = (flagName: string) => {
  const me = useMeShared();
  const localFeatureFlagValue = getLocalFeatureFlagValue(flagName);

  const context = {
    employerId: me
      ? 'companyId' in me.employer
        ? me.employer.companyId
        : me.employer.pk
      : undefined,
    employeeId: me?.id,
  };

  const { data, isInitialLoading, error } = useQuery({
    queryKey: getKey(context),
    queryFn: () => getFlags(context),
    staleTime: 10 * 1000, // 10 seconds
    cacheTime: 60 * 1000, // 1 minute,
    enabled:
      isNil(localFeatureFlagValue) &&
      !!context.employeeId &&
      !!context.employerId,
    refetchOnWindowFocus: false,
  });
  const flag = data?.toggles?.find((toggle) => toggle.name === flagName);

  return {
    isEnabled: localFeatureFlagValue ?? flag?.enabled ?? false,
    variant: flag?.variant,
    isLoading: isNil(localFeatureFlagValue) ? isInitialLoading : false,
    error,
  };
};

export default useFeatureFlag;
